import {
  useEffect,
  useId,
  useRef,
  useState,
  Children,
  isValidElement,
  cloneElement,
  type ComponentProps,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  X,
  Plus,
  Trash2,
  RotateCcw,
  Eye,
  EyeOff,
  Check,
  Download,
  Upload,
  Pencil,
  Sparkles,
  Copy,
  Search as SearchIcon,
  Send,
  ShieldCheck,
  LogOut,
  Settings2,
  type LucideIcon,
} from "lucide-react";
import { HelpTip } from "./HelpTip";
import { ConfigurationEditor } from "./ConfigurationEditor";
import { useAuthoringServices, useLabel } from "./services";
export { HelpTip };
import { SearchableSelect } from "./SearchableSelect";
import { Select } from "@proof/ui";
export { Select };
export {
  SearchableSelect,
  searchThreshold,
  searchPageSize,
  type SelectOption,
  type SelectPage,
  type SelectLoader,
} from "./SearchableSelect";
function PasswordControl({
  input,
  label,
}: {
  input: ReactElement<Record<string, unknown>>;
  label: string;
}) {
  const [visible, setVisible] = useState(false);
  const action = `${visible ? "Hide" : "Show"} ${label.toLowerCase()}`;
  return (
    <span className="password-input">
      {cloneElement(input, { type: visible ? "text" : "password" })}
      <button
        type="button"
        className="password-visibility"
        aria-label={action}
        title={action}
        aria-pressed={visible}
        aria-controls={String(input.props.id)}
        disabled={Boolean(input.props.disabled)}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => setVisible((value) => !value)}
      >
        {visible ? (
          <EyeOff size={18} aria-hidden="true" />
        ) : (
          <Eye size={18} aria-hidden="true" />
        )}
      </button>
    </span>
  );
}
/** Text of a button's label, ignoring any icon elements. */
function labelText(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(labelText).join("");
  if (isValidElement(node))
    return labelText(
      (node as ReactElement<{ children?: ReactNode }>).props.children,
    );
  return "";
}
/** True when the content already includes an icon (an <svg> element). */
function hasIcon(node: ReactNode): boolean {
  let found = false;
  Children.forEach(node, (child) => {
    if (found || !isValidElement(child)) return;
    if (typeof child.type !== "string" || child.type === "svg") found = true;
  });
  return found;
}
/** One place that pairs common actions with an icon, by the button's wording. */
const actionIcons: [RegExp, LucideIcon][] = [
  [/^(save|apply|confirm|done|accept|approve|assign)\b/i, Check],
  [/^(cancel|close|dismiss|discard)\b/i, X],
  [/^(delete|remove|archive)\b/i, Trash2],
  [/^(add|create|new|include|expose)\b/i, Plus],
  [/^(export|download)\b/i, Download],
  [/^(import|upload|load from file)\b/i, Upload],
  [/^(edit|rename|change|open)\b/i, Pencil],
  [/^(generate|suggest)\b/i, Sparkles],
  [/^(preview|view|show)\b/i, Eye],
  [/^(retry|refresh|reload|reconnect|restore|reset|undo)\b/i, RotateCcw],
  [/^(copy|duplicate)\b/i, Copy],
  [/^(search|find)\b/i, SearchIcon],
  [/^(publish|send|invite)\b/i, Send],
  [/^(test|check|validate|verify)\b/i, ShieldCheck],
  [/^(sign out|log out)\b/i, LogOut],
  [/^(settings|configure)\b/i, Settings2],
];
export function Button({
  children,
  onClick,
  kind = "",
  disabled = false,
  title,
  "aria-label": ariaLabel,
  "aria-current": ariaCurrent,
  type = "button",
  icon,
}: {
  children: ReactNode;
  onClick?: () => void;
  kind?: string;
  disabled?: boolean;
  title?: string;
  "aria-label"?: string;
  "aria-current"?: "page" | "step" | "true";
  type?: "button" | "submit";
  /** false suppresses the automatic action icon. */
  icon?: false;
}) {
  const relabel = useLabel();
  if (typeof children === "string") children = relabel(children);
  const label = labelText(children).trim();
  const Icon =
    icon === false || hasIcon(children)
      ? undefined
      : actionIcons.find(([pattern]) => pattern.test(label))?.[1];
  return (
    <button
      type={type}
      className={`button ${kind}`}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
      aria-current={ariaCurrent}
    >
      {Icon && <Icon size={14} aria-hidden="true" />}
      {children}
    </button>
  );
}
export function Control({
  label,
  children,
  note,
  reset,
  help,
  helpDetails,
  helpDocs,
}: {
  label: string;
  children: ReactNode;
  note?: string;
  reset?: () => void;
  /** Short "?" description; activating it opens `helpDetails` in a modal. */
  help?: string;
  helpDetails?: ReactNode;
  helpDocs?: string;
}) {
  label = useLabel()(label);
  const controlId = useId();
  const labeledChildren = Children.map(children, (child) => {
    if (isValidElement(child) && child.type === SearchableSelect) {
      const element = child as ReactElement<{ id?: string }>;
      return cloneElement(element, { id: element.props.id || controlId });
    }
    if (isValidElement(child) && child.type === Select) {
      const element = child as ReactElement<Record<string, unknown>>;
      return cloneElement(element, {
        id: element.props.id || controlId,
        "aria-label": element.props["aria-label"] || label,
        "aria-describedby": note
          ? `${controlId}-note`
          : element.props["aria-describedby"],
      });
    }
    if (
      !isValidElement(child) ||
      !["input", "select", "textarea"].includes(String(child.type))
    )
      return child;
    const element = child as ReactElement<Record<string, unknown>>;
    const labeled = cloneElement(element, {
      id: element.props.id || controlId,
      "aria-label": element.props["aria-label"] || label,
      "aria-describedby": note
        ? `${controlId}-note`
        : element.props["aria-describedby"],
    });
    return child.type === "input" && element.props.type === "password" ? (
      <PasswordControl
        input={labeled}
        label={String(labeled.props["aria-label"])}
      />
    ) : (
      labeled
    );
  });
  return (
    <div className="control">
      <span className="control-label">
        <label htmlFor={controlId}>{label}</label>
        {help && (
          <HelpTip title={label} summary={help} docs={helpDocs}>
            {helpDetails}
          </HelpTip>
        )}
        {reset && (
          <button
            type="button"
            className="reset-link"
            title={`Inherit ${label} from project`}
            onClick={(e) => {
              e.preventDefault();
              reset();
            }}
          >
            <RotateCcw size={11} />
            Inherit
          </button>
        )}
      </span>
      {labeledChildren}
      {note && (
        <small id={`${controlId}-note`} className="muted">
          {note}
        </small>
      )}
    </div>
  );
}
export function Toggle({
  label,
  value,
  onChange,
  disabled = false,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  label = useLabel()(label);
  return (
    <label className="toggle">
      <span>{label}</span>
      <input
        type="checkbox"
        role="switch"
        checked={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
    </label>
  );
}
export function Modal({
  title,
  children,
  onClose,
  wide = false,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  title = useLabel()(title);
  const backdrop = useRef<HTMLDivElement>(null);
  useEffect(() => {
    backdrop.current
      ?.querySelector<HTMLElement>("input,textarea,select,button")
      ?.focus();
  }, []);
  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if (!e.composedPath().includes(backdrop.current!)) return;
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);
  return (
    <div
      ref={backdrop}
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <section
        className={`modal ${wide ? "wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header>
          <h2>{title}</h2>
          <button
            className="icon-button"
            aria-label="Close dialog"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </header>
        {/* The header and trailing actions stay put; only this body scrolls. */}
        <div className="modal-body">{children}</div>
      </section>
    </div>
  );
}
/** JSON configuration editors; hosts can hide them with enableJsonConfiguration. */
export function JsonEditor(props: ComponentProps<typeof ConfigurationEditor>) {
  const presentation = useAuthoringServices().editorPresentation;
  if (presentation?.enableJsonConfiguration === false) return null;
  return <ConfigurationEditor {...props} />;
}
export type SetupMode = "guided" | "advanced" | "json";
/**
 * One switch for every setup surface: a guided editor first, the full
 * control-by-control setup second, and raw JSON for exact configuration.
 */
export function SetupModes({
  label,
  value,
  onChange,
  guidedLabel = "Guided setup",
  modes = ["guided", "advanced", "json"],
}: {
  label: string;
  value: SetupMode;
  onChange: (mode: SetupMode) => void;
  guidedLabel?: string;
  modes?: SetupMode[];
}) {
  const presentation = useAuthoringServices().editorPresentation;
  const allowed = modes.filter(
    (mode) =>
      !(mode === "advanced" && presentation?.enableAdvancedSetup === false) &&
      !(mode === "json" && presentation?.enableJsonConfiguration === false),
  );
  // A hidden mode never stays selected; editing continues in an allowed one.
  useEffect(() => {
    if (allowed.length && !allowed.includes(value)) onChange(allowed[0]);
  }, [allowed.join(), value]);
  const names: Record<SetupMode, string> = {
    guided: guidedLabel,
    advanced: "Advanced setup",
    json: "JSON",
  };
  if (allowed.length < 2) return null;
  return (
    <div className="setup-modes" role="group" aria-label={label}>
      {allowed.map((mode) => (
        <button
          key={mode}
          type="button"
          aria-pressed={value === mode}
          className={value === mode ? "active" : ""}
          onClick={() => onChange(mode)}
        >
          {names[mode]}
        </button>
      ))}
    </div>
  );
}
/** Plain name/value rows for string maps such as PDF metadata. */
export function KeyValueEditor({
  label,
  value,
  onChange,
  keyLabel = "Name",
  valueLabel = "Value",
  addLabel = "Add row",
}: {
  label: string;
  value: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
  keyLabel?: string;
  valueLabel?: string;
  addLabel?: string;
}) {
  const rows = Object.entries(value);
  const write = (next: [string, string][]) =>
    onChange(Object.fromEntries(next));
  return (
    <div className="key-value-editor" role="group" aria-label={label}>
      {rows.map(([key, text], index) => (
        <div className="key-value-row" key={index}>
          <input
            aria-label={`${keyLabel} ${index + 1}`}
            placeholder={keyLabel}
            value={key}
            onChange={(e) =>
              write(
                rows.map((row, i) =>
                  i === index ? [e.target.value, row[1]] : row,
                ),
              )
            }
          />
          <input
            aria-label={`${valueLabel} ${index + 1}`}
            placeholder={valueLabel}
            value={text}
            onChange={(e) =>
              write(
                rows.map((row, i) =>
                  i === index ? [row[0], e.target.value] : row,
                ),
              )
            }
          />
          <button
            type="button"
            className="icon-button"
            aria-label={`Remove ${key || `row ${index + 1}`}`}
            onClick={() => write(rows.filter((_, i) => i !== index))}
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button
        type="button"
        className="text-button"
        disabled={Object.hasOwn(value, "")}
        onClick={() => write([...rows, ["", ""]])}
      >
        <Plus size={13} /> {addLabel}
      </button>
    </div>
  );
}
export function Empty({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <div className="empty-symbol">
        <Plus size={24} />
      </div>
      <h3>{title}</h3>
      <p>{description}</p>
      {action}
    </div>
  );
}
export function Chips({
  items,
  onRemove,
}: {
  items: string[];
  onRemove: (i: number) => void;
}) {
  return (
    <div className="chips">
      {items.map((item, i) => (
        <span className="chip" key={i}>
          {item}
          <button title="Remove" onClick={() => onRemove(i)}>
            <Trash2 size={12} />
          </button>
        </span>
      ))}
    </div>
  );
}
/** A non-negative amount shown as currency; editing shows the plain number. */
export function CurrencyInput({
  value,
  onChange,
  currency = "USD",
  id,
  "aria-label": ariaLabel,
}: {
  value: number;
  onChange: (value: number) => void;
  currency?: string;
  id?: string;
  "aria-label"?: string;
}) {
  const [editing, setEditing] = useState<string>();
  const formatted = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value || 0);
  return (
    <input
      id={id}
      aria-label={ariaLabel}
      type="text"
      inputMode="decimal"
      className="numeric"
      value={editing ?? formatted}
      onFocus={() => setEditing(value ? String(value) : "")}
      onChange={(event) => {
        setEditing(event.target.value);
        const amount = Number(event.target.value.replace(/[^\d.]/g, ""));
        if (Number.isFinite(amount)) onChange(Math.max(0, amount));
      }}
      onBlur={() => setEditing(undefined)}
    />
  );
}
